/**
 * Application Entry Point
 * 
 * This is the main entry file that initializes the React application.
 * Handles:
 * - React DOM rendering
 * - Redux store provider
 * - Theme context provider
 * - Performance monitoring
 */

import React from "react";
import ReactDOM from "react-dom/client";
import { ensureSentryLoaded } from "./loadSentry";
import "./index.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "primereact/resources/themes/lara-light-blue/theme.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import "./styles/bodyTheme.css";
import "./styles/catalog-browse.css";
import "./styles/writers.css";
import "./styles/platform-shell.css";
import { Provider } from "react-redux";
import store from "./redux/store";
import { ThemeProvider } from "./contexts/ThemeContext";
import { captureError } from "./observability/logger";
import { reportVital } from "./observability/webVitalsReporter";

// ============================================
// React Root Element
// ============================================
const root = ReactDOM.createRoot(document.getElementById("root"));

// ============================================
// Application Rendering
// ============================================
/**
 * Renders the React application with all providers
 *
 * Provider Hierarchy:
 * 1. Redux Provider - Global state management
 * 2. Theme Provider - Theme switching (light/dark)
 * 3. App Component - Main application component
 *
 * StrictMode: Enabled for development to catch potential issues
 */
function renderApp() {
  root.render(
    <React.StrictMode>
      <Provider store={store}>
        <ThemeProvider>
          <App />
        </ThemeProvider>
      </Provider>
    </React.StrictMode>
  );
}

ensureSentryLoaded().finally(renderApp);

if (typeof window !== "undefined") {
  window.addEventListener("error", (event) => {
    captureError(event?.error || event?.message || "Unhandled window error", { source: "window.error" });
  });
  window.addEventListener("unhandledrejection", (event) => {
    captureError(event?.reason || "Unhandled promise rejection", { source: "window.unhandledrejection" });
  });
}

// ============================================
// Performance Monitoring
// ============================================
/**
 * Web Vitals reporting for performance monitoring
 * 
 * Metrics tracked:
 * - Largest Contentful Paint (LCP)
 * - First Input Delay (FID)
 * - Cumulative Layout Shift (CLS)
 * 
 * To send metrics to analytics service:
 * reportWebVitals((metric) => {
 *   // Send to analytics endpoint
 * });
 */
reportWebVitals(reportVital);
